

import React from "react";
import { motion } from "framer-motion";
import {
  FaBookOpen,
  FaHeartbeat,
  FaTractor,
  FaBullhorn,
  FaFemale,
  FaHandsHelping,
  FaTheaterMasks,
  FaHandHoldingHeart,
  FaNewspaper,
  FaPrayingHands,
  FaUsers,
} from "react-icons/fa";

const initiatives = [
  {
    icon: <FaBookOpen className="text-5xl text-brandRed mr-4" />,
    title: "Public Education and Training",
    description:
      "Establish educational institutions, promote adult education, and implement training programs for youth and professionals.",
  },
  {
    icon: <FaHeartbeat className="text-5xl text-brandRed mr-4" />,
    title: "Health and Medical Initiatives",
    description:
      "Operate healthcare facilities, provide medical assistance, and support mental health and rehabilitation programs.",
  },
  {
    icon: <FaTractor className="text-5xl text-brandRed mr-4" />,
    title: "Agricultural and Rural Development",
    description:
      "Promote agricultural activities and launch self-help programs to enhance social and economic standards.",
  },
  {
    icon: <FaBullhorn className="text-5xl text-brandRed mr-4" />,
    title: "Social Awareness and Empowerment",
    description:
      "Educate vulnerable groups about their rights and organize community groups to combat exploitation.",
  },
  {
    icon: <FaFemale className="text-5xl text-brandRed mr-4" />,
    title: "Women's and Children Support",
    description:
      "Focus on development and education, and establish homes providing shelter and essential services.",
  },
  {
    icon: <FaHandsHelping className="text-5xl text-brandRed mr-4" />,
    title: "Community Collaboration and Civic Engagement",
    description:
      "Foster collaboration among organizations and conduct recreational activities with scholarships and awards.",
  },
  {
    icon: <FaTheaterMasks className="text-5xl text-brandRed mr-4" />,
    title: "Cultural Development",
    description:
      "Advance the arts by conducting programs and providing classes for non-professional artists.",
  },
  {
    icon: <FaHandHoldingHeart className="text-5xl text-brandRed mr-4" />,
    title: "Disaster Relief and Support",
    description:
      "Assist individuals affected by natural disasters, providing financial and material help.",
  },
  {
    icon: <FaNewspaper className="text-5xl text-brandRed mr-4" />,
    title: "Publication and Dissemination",
    description:
      "Publish literature on health, medicine, and social issues using various media platforms.",
  },
  {
    icon: <FaPrayingHands className="text-5xl text-brandRed mr-4" />,
    title: "Socio-Spiritual Activities",
    description:
      "Conduct study groups and seminars to promote socio-spiritual development within the community.",
  },
  {
    icon: <FaUsers className="text-5xl text-brandRed mr-4" />,
    title: "Comprehensive Support Services",
    description:
      "Establish centers for senior citizens and support families and individuals in distress.",
  },
];

const cardVariant = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
};

const InitiativeCard = ({ icon, title, description, index }) => {
  return (
    <motion.div
      variants={cardVariant}
      initial="initial"
      animate="animate"
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="w-[300px] h-[280px] bg-white rounded-xl shadow-md p-5 flex-shrink-0 flex flex-col justify-between text-left mx-3 border border-gray-200 transition-all duration-300 hover:shadow-2xl hover:border-brandRed hover:-translate-y-2 hover:bg-gray-50"
      whileHover={{ scale: 1.07 }}
    >
      <div className="flex items-center mb-3">
        {icon}
        <h3 className="text-lg font-semibold text-brandRed">{title}</h3>
      </div>
      <div className="flex-grow">
        <p className="text-gray-600 text-md md:text-base leading-relaxed">
          {description}
        </p>
      </div>
    </motion.div>
  );
};

const InitiativesCarousel = () => {
  return (
    <div className="relative overflow-hidden">
      {/* Top Decorative Triangle */}
      <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 mb-[2rem]">
        <div className="w-0 h-0 border-l-[100px] md:border-l-[300px] border-l-transparent border-r-[100px] md:border-r-[300px] border-r-transparent border-b-[150px] md:border-b-[300px] border-b-gray-100"></div>
      </div>

      <section className="py-16 bg-gray-100">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-semibold text-brandRed relative inline-block group">
            <span className="relative z-10">Our Initiatives</span>
            <span className="absolute left-0 bottom-0 h-1 bg-brandRed transition-all duration-300 w-0 group-hover:w-full"></span>
          </h2>
        </div>

        <div className="overflow-hidden">
          <motion.div
            className="flex space-x-[40px]"
            initial={{ x: "100%" }}
            animate={{ x: "-100%" }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          >
            {initiatives.map((item, index) => (
              <InitiativeCard
                key={index}
                icon={item.icon}
                title={item.title}
                description={item.description}
                index={index}
              />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Bottom Decorative Triangle */}
      <div className="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2 mb-[8rem] mt-[6rem]">
        <div className="w-0 h-0 border-l-[60px] md:border-l-[200px] border-l-transparent border-r-[80px] md:border-r-[200px] border-r-transparent border-t-[120px] md:border-t-[300px] border-t-gray-100"></div>
      </div>
    </div>
  );
};

export default InitiativesCarousel;
